import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { DetroitGameState, SelectActionArgs, DetroitActionType } from '../../types';
import { getDetroitActionFee } from '../../types';
import { validateSelectActionDetroit } from '../validators/selectActionsDetroitValidator';
import { createSkipAction } from './common/skipAction';

/**
 * アクション選択Move（Detroit固有）
 *
 * @param G - Detroitゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - アクション選択引数
 * @returns void or INVALID_MOVE
 *
 * 処理内容:
 * 1. バリデーション
 * 2. 手数料支払い（現金不足時は収入レベルから減算）
 * 3. ディスク1枚削除
 * 4. アクション選択を記録
 * 5. LOCOMOTIVEの場合は即座に機関車レベル+1
 */
export function selectActionDetroit(
  { G, ctx }: { G: DetroitGameState; ctx: Ctx },
  args: SelectActionArgs
): void | typeof INVALID_MOVE {
  // バリデーション
  const validation = validateSelectActionDetroit(G, ctx, args);
  if (!validation.success) {
    console.error(`[selectActionDetroit] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const player = G.players['0'];
  const action = args.action as DetroitActionType;
  const disksRemaining = G.actionDisks[action];
  const fee = getDetroitActionFee(disksRemaining);

  // 手数料支払い
  if (player.cash >= fee) {
    player.cash -= fee;
    debug.log(`[selectActionDetroit] Player paid $${fee} fee, remaining cash: $${player.cash}`);
  } else {
    // 現金不足の場合、収入レベルから減算
    const shortage = fee - player.cash;
    player.cash = 0;
    player.incomeLevel -= shortage;
    debug.log(`[selectActionDetroit] Player insufficient cash, shortage: $${shortage}, incomeLevel: ${player.incomeLevel}`);

    // 収入レベルが負になった場合は破産
    if (player.incomeLevel < 0) {
      player.isBankrupt = true;
      G.isGameOver = true;
      G.endReason = 'BANKRUPTCY';
      player.finalScore = G.actualTurn;
      player.isVictorious = G.actualTurn >= 7;
      debug.log(`[selectActionDetroit] Player BANKRUPT during action selection at turn ${G.actualTurn}`);
    }
  }

  // ディスク1枚削除
  G.actionDisks[action] -= 1;

  // アクション選択を記録
  player.selectedAction = action;

  // Locomotiveアクションの即座実行
  if (action === 'LOCOMOTIVE') {
    if (player.locomotiveLevel < 6) {
      player.locomotiveLevel += 1;
      debug.log(`[selectActionDetroit] Player upgraded locomotive to level ${player.locomotiveLevel}`);
    } else {
      debug.log(`[selectActionDetroit] Locomotive already at max level (6), action selected but no upgrade`);
    }
  }

  debug.log(`[selectActionDetroit] Player selected action: ${action}, fee: $${fee}, disks remaining: ${JSON.stringify(G.actionDisks)}`);
}

/**
 * アクション選択スキップMove（共通モジュール使用）
 * @see data/map/detroit_bankruptcy_solo/detroit_bankruptcy_solo_rules.md 61行目
 */
export const skipAction = createSkipAction<DetroitGameState>({
  onComplete: () => {
    // Detroit: hasPassed は共通モジュール内で設定済み、追加処理なし
  }
});
